import React, { useState } from 'react'
import styled from 'styled-components/macro'
import { connect } from 'react-redux'
import useTranslation from 'next-translate/useTranslation'

import { Text } from '@/components/Text'
import { PageLayout } from '@/components/Layout/PageLayout'

import { ColorsMap, FontSizeMap, device } from '@/utils/Theme'

import { StateInterface } from '@/types/State'
import { sets } from '@/static/Sets'
import { InputText } from '@/components/InputText'
import Icons from '@/components/Icons'

import SpecialOfferSetCard from './parts/SpecialOfferSetCard'
import { Button } from '@/components/Button'
import { TextArea } from '@/components/Inputs'

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 14%;
  margin-bottom: 4rem;
`

const BannerWrapper = styled.div`
  width: 100%;
  height: fit-content;

  > img {
    width: 100%;
  }
`

const IntroWrapper = styled.div`
  padding: 2rem;
  background-color: ${ColorsMap.white};
  margin-bottom: 4rem;
  margin-top: 4rem;
`

const SetsWrapper = styled.div`
  display: grid;
  grid-template-columns: 100%;
  column-gap: 2rem;
  row-gap: 4rem;
  margin-bottom: 4rem;

  @media (min-width: ${device.tablet}) {
    grid-template-columns: 1fr 1fr;
  }
`

const FormWrapper = styled.div`
  display: grid;
  grid-template-columns: 5fr 2fr;
  gap: 4rem;
  padding: 3rem;
  background-color: ${ColorsMap.white};
  margin-bottom: 4rem;

  @media (max-width: ${device.tablet}) {
    grid-template-columns: 1fr;
  }
`

const ContactWrapper = styled.div`
  padding: 3rem;
  background-color: ${ColorsMap.primary};

  @media (max-width: ${device.tablet}) {
    padding: 2rem;
  }
`

const InputGroup = styled.div`
  display: flex;
  column-gap: 2rem;

  > * {
    width: 100%;
  }

  @media (max-width: ${device.tablet}) {
    flex-direction: column;
  }
`
const Separator = styled.hr`
  margin: 1rem 0;
  border: 1px solid ${ColorsMap.lightGrey};
`

const SpecialOfferPage = ({ state }) => {
  const { t } = useTranslation('common')

  const [name, setName] = useState('')
  const [club, setClub] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [content, setContent] = useState('')

  const [nameInvalid, setNameInvalid] = useState(false)
  const [clubInvalid, setClubInvalid] = useState(false)
  const [emailInvalid, setEmailInvalid] = useState(false)
  const [phoneInvalid, setPhoneInvalid] = useState(false)
  const [contentInvalid, setContentInvalid] = useState(false)

  const [buttonDisabled, setButtonDisabled] = useState(false)

  const validateForm = () => {
    if (!name) {
      setNameInvalid(true)
    } else {
      setNameInvalid(false)
    }
    if (!club) {
      setClubInvalid(true)
    } else {
      setClubInvalid(false)
    }
    if (!email) {
      setEmailInvalid(true)
    } else {
      setEmailInvalid(false)
    }
    if (!phone) {
      setPhoneInvalid(true)
    } else {
      setPhoneInvalid(false)
    }
    if (!content) {
      setContentInvalid(true)
    } else {
      setContentInvalid(false)
    }

    if (!nameInvalid && !clubInvalid && !emailInvalid && !phoneInvalid && !contentInvalid) {
      return true
    }
    return false
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    setButtonDisabled(true)
    if (validateForm()) {
      const data = {
        name,
        club,
        email,
        phone,
        message: content,
      }

      await fetch('/api/email', {
        method: 'post',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            alert(t('message__form_success'))
            setName('')
            setClub('')
            setEmail('')
            setPhone('')
            setContent('')
          }
        })
    }
    setButtonDisabled(false)
  }

  return (
    <PageLayout secondary title={`${t('title:clubs-offer')} | ${state.settings.name}`}>
      <BannerWrapper>
        <img src={'/images/banners/clubs-offer.jpg'} alt="" />
      </BannerWrapper>
      <Wrapper>
        <IntroWrapper>
          <Text textAlign="center" textColor={ColorsMap.primary} fontWeight={700} fontSize={FontSizeMap.xl} mb="1rem">
            {t('special-offer-title')}
          </Text>
          <Text textAlign="justify">{t('special-offer-content')}</Text>
        </IntroWrapper>
        <SetsWrapper>
          {sets &&
            sets.map((set, i) => <SpecialOfferSetCard name={set.name} position={i + 1} imageSrc={set.imageSrc} />)}
        </SetsWrapper>
        <FormWrapper>
          <form action="." method="POST" onSubmit={handleSubmit}>
            <Text textColor="primary" fontWeight="bold" fontSize="xl">
              {t('special-offer-form-title')}
            </Text>
            <InputGroup>
              <InputText
                label={
                  <Text textColor="primary" fontWeight="bold">
                    {t('name')}
                  </Text>
                }
                name="name"
                id="name"
                value={name}
                invalid={nameInvalid}
                onChange={(e) => setName(e.target.value)}
              />
              <InputText
                label={
                  <Text textColor="primary" fontWeight="bold">
                    {t('club-name')}
                  </Text>
                }
                name="club_name"
                id="club_name"
                value={club}
                invalid={clubInvalid}
                onChange={(e) => setClub(e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <InputText
                label={
                  <Text textColor="primary" fontWeight="bold">
                    {t('email')}
                  </Text>
                }
                name="email"
                id="email"
                value={email}
                invalid={emailInvalid}
                onChange={(e) => setEmail(e.target.value)}
              />
              <InputText
                label={
                  <Text textColor="primary" fontWeight="bold">
                    {t('mobile-number')}
                  </Text>
                }
                name="phone"
                id="phone"
                value={phone}
                invalid={phoneInvalid}
                onChange={(e) => setPhone(e.target.value)}
              />
            </InputGroup>
            <TextArea
              name="content"
              id="content"
              onChange={(e) => setContent(e.target.value)}
              value={content}
              invalid={contentInvalid}
              my="1rem"
            >
              <Text textColor="primary" fontWeight="bold" mt="1rem">
                {t('content')}
              </Text>
            </TextArea>
            <Button width="100%" variant="horizontalRad" colorVariant="primary" type="submit" disabled={buttonDisabled}>
              {t('send')}
            </Button>
          </form>
          <ContactWrapper>
            <Text textColor="white" fontWeight="bold" fontSize="xl" mb="1rem">
              {t('contact-info')}
            </Text>
            <Separator />
            <Text textColor="white" mb="1rem">
              <Icons.Envelope color="white" mr="0.5rem" />
              milos@tn.sintra.sk
            </Text>
            <Text textColor="white">
              <Icons.Phone color="white" mr="0.5rem" />
              +421 907 846 200
            </Text>
            <Separator />
            <Text textColor="white" fontWeight="bold">
              SINTRA šport, s.r.o.
            </Text>
            <Text textColor="white">Zlatovská 1962</Text>
            <Text textColor="white">911 05 Trenčín</Text>
            <Text textColor="white">{t('slovakia')}</Text>
          </ContactWrapper>
        </FormWrapper>
      </Wrapper>
    </PageLayout>
  )
}

const mapStateToProps = (state: StateInterface) => {
  return {
    state,
  }
}

export default connect(mapStateToProps)(SpecialOfferPage)

